import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { NarrativeInvalidation } from "@xhalo/story-contracts";

import {
  acceptPendingChange,
  createBranch,
  createUnit,
  decideInvalidation,
  loadBranches,
  loadCommits,
  loadInvalidations,
  loadStudio,
  loadWorld,
  proposeDraft,
  rejectPendingChange,
  saveDraft,
  StoryClientError,
  type PendingChange,
  type StudioAuth,
  type StudioSnapshot,
  type StoryBranch,
  type StoryCommit,
  type WorldState,
} from "../api/client";
import { createStoryTelemetry } from "../api/telemetry";
import { InspectorPanel } from "../components/InspectorPanel";
import { ContextAiPanel } from "../components/ContextAiPanel";
import { IntelligencePanel } from "../components/IntelligencePanel";
import { ProductionPanel } from "../components/ProductionPanel";
import { CollaborationWorldGamePanel } from "../components/CollaborationWorldGamePanel";
import { ManuscriptEditor } from "../components/ManuscriptEditor";
import { type MobileView, MobileNav } from "../components/MobileNav";
import { OutlinePanel } from "../components/OutlinePanel";
import { StatusBar } from "../components/StatusBar";
import { TopBar } from "../components/TopBar";
import { WorkspaceNav } from "../components/WorkspaceNav";
import type { WorkspaceView } from "../components/WorkspaceNav";
import { WorldPanel } from "../components/WorldPanel";
import { VersionPanel } from "../components/VersionPanel";
import { proposedContent, scenes, type Scene } from "../data/story";
import { useStoryPreferences, type StoryMessageKey } from "../i18n";

const runtimeApiOrigin = import.meta.env.VITE_STORY_API_ORIGIN?.trim() || "http://127.0.0.1:8787";
const runtimeProjectId = import.meta.env.VITE_STORY_PROJECT_ID?.trim() || "";
const previewActor = import.meta.env.VITE_STORY_PREVIEW_ACTOR?.trim();
const previewDisplayName = import.meta.env.VITE_STORY_PREVIEW_DISPLAY_NAME?.trim();
const runtimeAuth: StudioAuth = {
  ...(previewActor ? { previewActor } : {}),
  ...(previewDisplayName ? { previewDisplayName } : {}),
};
const emptyWorld: WorldState = { facts: [], events: [], knowledge: [], beliefs: [], relationships: [], promises: [] };

export type AppConfig = { apiOrigin: string; projectId: string; auth: StudioAuth; branchName?: string };
type AppProps = { config?: AppConfig };
const runtimeConfig: AppConfig = { apiOrigin: runtimeApiOrigin, projectId: runtimeProjectId, auth: runtimeAuth };

const demoPending: PendingChange = {
  id: "change-demo-c018",
  unitId: "scene-2",
  baseCommitId: "C-018",
  proposedContent: proposedContent.join("\n\n"),
  proposedContentHash: "demo",
  narrativeDelta: {},
  provenance: { source: "demo" },
  status: "pending",
  createdAt: Date.now(),
};

function splitContent(content: string): string[] {
  const paragraphs = content.split(/\n\s*\n/u).map((value) => value.trim()).filter(Boolean);
  return paragraphs.length > 0 ? paragraphs : [""];
}

function shortHead(value: string): string {
  return value.length > 14 ? value.slice(0, 12) : value;
}

function errorMessage(error: unknown, t: (key: StoryMessageKey) => string): string {
  if (error instanceof StoryClientError) {
    if (error.code === "HEAD_CONFLICT") return t("headConflict");
    if (error.code === "AUTH_REQUIRED") return t("authFailed");
    return error.message;
  }
  return error instanceof Error ? error.message : t("temporaryUnavailable");
}

export function App({ config = runtimeConfig }: AppProps) {
  const { t } = useStoryPreferences();
  const { apiOrigin, projectId, auth: baseAuth } = config;
  const remoteEnabled = projectId.length > 0;
  const [activeBranchName, setActiveBranchName] = useState(config.branchName ?? "main");
  const [branches, setBranches] = useState<StoryBranch[]>([]);
  const [commits, setCommits] = useState<StoryCommit[]>([]);
  const auth = useMemo<StudioAuth>(() => ({ ...baseAuth, branchName: activeBranchName }), [activeBranchName, baseAuth]);
  const [snapshot, setSnapshot] = useState<StudioSnapshot | null>(null);
  const [world, setWorld] = useState<WorldState>(emptyWorld);
  const [invalidations, setInvalidations] = useState<NarrativeInvalidation[]>([]);
  const [selectedId, setSelectedId] = useState("scene-2");
  const [demoHead, setDemoHead] = useState("C-018");
  const [decision, setDecision] = useState<"pending" | "accepted" | "rejected" | "empty">(remoteEnabled ? "empty" : "pending");
  const [acceptedContent, setAcceptedContent] = useState<string[] | null>(null);
  const [draftOverrides, setDraftOverrides] = useState<Record<string, string>>({});
  const [dirtyDraft, setDirtyDraft] = useState<{ unitId: string; content: string } | null>(null);
  const [saved, setSaved] = useState(true);
  const saveQueue = useRef<Promise<unknown>>(Promise.resolve());
  const [loading, setLoading] = useState(remoteEnabled);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [mobileView, setMobileView] = useState<MobileView>("write");
  const [workspaceView, setWorkspaceView] = useState<WorkspaceView>("write");
  const telemetry = useMemo(() => remoteEnabled && import.meta.env.MODE !== "test" ? createStoryTelemetry({ apiOrigin, projectId, auth }) : null,
    [apiOrigin, auth, projectId, remoteEnabled]);

  useEffect(() => {
    if (!telemetry) return;
    telemetry.track("studio_opened", "write");
    if (telemetry.returning) telemetry.track("return_visit", "write");
    void telemetry.flush();
    return () => telemetry.dispose();
  }, [telemetry]);

  const refresh = useCallback(async (signal?: AbortSignal, preferredUnitId?: string) => {
    if (!remoteEnabled) return;
    setLoading(true);
    try {
      const [nextSnapshot, nextWorld, nextInvalidations, nextBranches, nextCommits] = await Promise.all([
        loadStudio({ apiOrigin, projectId, auth, ...(preferredUnitId ? { unitId: preferredUnitId } : {}), ...(signal ? { signal } : {}) }),
        loadWorld({ apiOrigin, projectId, auth, ...(signal ? { signal } : {}) }),
        loadInvalidations({ apiOrigin, projectId, auth, ...(signal ? { signal } : {}) }),
        loadBranches({ apiOrigin, projectId, auth, ...(signal ? { signal } : {}) }),
        loadCommits({ apiOrigin, projectId, auth, ...(signal ? { signal } : {}) }),
      ]);
      setSnapshot(nextSnapshot);
      setWorld(nextWorld);
      setInvalidations(nextInvalidations);
      setBranches(nextBranches);
      setCommits(nextCommits);
      setSelectedId((current) => {
        return nextSnapshot.units.some((unit) => unit.id === current)
          ? current
          : preferredUnitId ?? nextSnapshot.pendingChanges[0]?.unitId ?? nextSnapshot.selectedUnit?.id ?? nextSnapshot.units[0]?.id ?? "";
      });
      setActionError(null);
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return;
      setActionError(errorMessage(error, t));
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [apiOrigin, auth, projectId, remoteEnabled, t]);

  useEffect(() => {
    if (!remoteEnabled) return;
    const controller = new AbortController();
    void refresh(controller.signal);
    return () => controller.abort();
  }, [refresh]);

  const remoteScenes = useMemo<Scene[]>(() => snapshot?.units.map((unit) => ({
    id: unit.id,
    label: unit.title.replace(" · ", "　"),
    title: unit.title,
    content: splitContent(draftOverrides[unit.id] ?? unit.draftContent ?? unit.content),
    nested: unit.parentId !== null || unit.type === "scene",
  })) ?? [], [draftOverrides, snapshot]);
  const activeScenes = remoteEnabled ? remoteScenes : scenes.map((scene) => ({
    ...scene,
    content: Object.hasOwn(draftOverrides, scene.id) ? splitContent(draftOverrides[scene.id]!) : scene.content,
  }));
  const totalWords = useMemo(() => activeScenes.reduce((total, scene) => total + scene.content.join("").length, 0), [activeScenes]);
  const selectedScene = useMemo(
    () => activeScenes.find((scene) => scene.id === selectedId) ?? activeScenes[0] ?? scenes[3]!,
    [activeScenes, selectedId],
  );
  const remoteUnit = snapshot?.units.find((unit) => unit.id === selectedScene.id) ?? null;
  const visibleContent = remoteEnabled
    ? selectedScene.content
    : selectedId === "scene-2" && acceptedContent && !Object.hasOwn(draftOverrides, selectedId) ? acceptedContent : selectedScene.content;
  const pendingChange = remoteEnabled
    ? snapshot?.pendingChanges.find((change) => change.unitId === selectedScene.id)
    : decision === "pending" && selectedScene.id === demoPending.unitId ? demoPending : undefined;
  const head = remoteEnabled ? snapshot?.branch.headCommitId ?? "—" : demoHead;
  const reviewStatus = pendingChange ? "pending" : decision === "pending" ? "empty" : decision;
  const openWorkspace = (view: WorkspaceView) => { setWorkspaceView(view); setMobileView("write"); };
  const openInvalidations = invalidations.filter((item) => item.status === "open" || item.status === "acknowledged");
  const factLabels = world.facts.map((fact) => `${String(fact.subject_id ?? "未知主体")} ${String(fact.predicate ?? "关联")} ${JSON.stringify(fact.object ?? null)}`);
  const canPropose = remoteEnabled && Boolean(remoteUnit) && !pendingChange
    && (Object.hasOwn(draftOverrides, selectedScene.id) || remoteUnit?.draftContent !== null);

  useEffect(() => {
    if (saved) return;
    const preventUnload = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", preventUnload);
    return () => window.removeEventListener("beforeunload", preventUnload);
  }, [saved]);

  useEffect(() => {
    if (!dirtyDraft) return;
    const currentDraft = dirtyDraft;
    let cancelled = false;
    const timer = window.setTimeout(() => {
      if (!remoteEnabled || !snapshot) {
        if (!cancelled) {
          setSaved(true);
          setDirtyDraft(null);
        }
        return;
      }
      const request = saveQueue.current.catch(() => undefined).then(() => {
        if (cancelled) return;
        return saveDraft({
          apiOrigin,
          projectId,
          unitId: currentDraft.unitId,
          baseCommitId: snapshot.branch.headCommitId,
          content: currentDraft.content,
          auth,
        });
      });
      saveQueue.current = request;
      void request.then(() => {
        if (!cancelled) {
          telemetry?.track("draft_saved", "write", { unitId: currentDraft.unitId, properties: { changed: true } });
          setSaved(true);
          setActionError(null);
          setDirtyDraft((value) => value === currentDraft ? null : value);
        }
      }).catch((error: unknown) => {
        if (!cancelled) setActionError(errorMessage(error, t));
      });
    }, 700);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [apiOrigin, auth, dirtyDraft, projectId, remoteEnabled, snapshot, t, telemetry]);

  const selectScene = (id: string) => {
    if (id === selectedId) return;
    if (busy || (remoteEnabled && !saved)) { setActionError(t("saveBeforeNavigation")); return; }
    setSelectedId(id);
    setWorkspaceView("write");
    setMobileView("write");
    setActionError(null);
    if (remoteEnabled) {
      const hasPending = snapshot?.pendingChanges.some((change) => change.unitId === id) ?? false;
      setDecision(hasPending ? "pending" : "empty");
      void refresh(undefined, id);
    }
  };

  const edit = (content: string) => {
    setSaved(false);
    setDraftOverrides((current) => ({ ...current, [selectedScene.id]: content }));
    setDirtyDraft({ unitId: selectedScene.id, content });
  };

  const propose = async () => {
    if (busy || !remoteEnabled || !snapshot || !remoteUnit || pendingChange) return;
    setBusy(true);
    setActionError(null);
    const content = draftOverrides[remoteUnit.id] ?? remoteUnit.draftContent ?? remoteUnit.content;
    try {
      await saveQueue.current.catch(() => undefined);
      await saveDraft({
        apiOrigin,
        projectId,
        unitId: remoteUnit.id,
        baseCommitId: snapshot.branch.headCommitId,
        content,
        auth,
      });
      await proposeDraft({
        apiOrigin,
        projectId,
        unitId: remoteUnit.id,
        baseCommitId: snapshot.branch.headCommitId,
        content,
        auth,
      });
      setDirtyDraft(null);
      setSaved(true);
      await refresh(undefined, remoteUnit.id);
      setDecision("pending");
      telemetry?.track("pending_change_opened", "review", { unitId: remoteUnit.id });
      setMobileView("changes");
    } catch (error) {
      setActionError(errorMessage(error, t));
      if (error instanceof StoryClientError && error.code === "HEAD_CONFLICT") await refresh(undefined, remoteUnit.id);
    } finally {
      setBusy(false);
    }
  };

  const accept = async () => {
    if (busy || !saved) { setActionError(t("saveBeforeNavigation")); return; }
    if (!remoteEnabled) {
      setAcceptedContent(proposedContent);
      setDecision("accepted");
      setDemoHead("C-019");
      setSaved(true);
      return;
    }
    if (!snapshot || !pendingChange) return;
    setBusy(true);
    setActionError(null);
    try {
      await acceptPendingChange({
        apiOrigin,
        projectId,
        changeId: pendingChange.id,
        expectedHead: snapshot.branch.headCommitId,
        auth,
      });
      setDraftOverrides((current) => {
        const next = { ...current };
        delete next[pendingChange.unitId];
        return next;
      });
      await refresh(undefined, pendingChange.unitId);
      setDecision("accepted");
      telemetry?.track("pending_change_accepted", "review", { unitId: pendingChange.unitId, properties: { result: "accepted" } });
      setMobileView("write");
    } catch (error) {
      setActionError(errorMessage(error, t));
      if (error instanceof StoryClientError && error.code === "HEAD_CONFLICT") await refresh(undefined, pendingChange.unitId);
    } finally {
      setBusy(false);
    }
  };

  const reject = async () => {
    if (busy) return;
    if (!remoteEnabled) {
      setDecision("rejected");
      return;
    }
    if (!pendingChange) return;
    setBusy(true);
    setActionError(null);
    try {
      await rejectPendingChange({
        apiOrigin,
        projectId,
        changeId: pendingChange.id,
        auth,
      });
      await refresh(undefined, pendingChange.unitId);
      setDecision("rejected");
      telemetry?.track("pending_change_rejected", "review", { unitId: pendingChange.unitId, properties: { result: "rejected" } });
    } catch (error) {
      setActionError(errorMessage(error, t));
    } finally {
      setBusy(false);
    }
  };

  const resolveInvalidation = async (id: string) => {
    if (!remoteEnabled) return;
    setBusy(true);
    try {
      await decideInvalidation({
        apiOrigin,
        projectId,
        invalidationId: id,
        status: "resolved",
        auth,
      });
      setInvalidations((items) => items.map((item) => item.id === id ? { ...item, status: "resolved", resolvedAt: Date.now() } : item));
    } catch (error) {
      setActionError(errorMessage(error, t));
    } finally {
      setBusy(false);
    }
  };

  const switchBranch = (name: string) => {
    if (name === activeBranchName) return;
    if (busy || !saved) { setActionError(t("saveBeforeNavigation")); return; }
    setSnapshot(null);
    setWorld(emptyWorld);
    setInvalidations([]);
    setDraftOverrides({});
    setSelectedId("");
    setActiveBranchName(name);
  };

  const addBranch = async () => {
    if (!remoteEnabled) return;
    if (busy || !saved) { setActionError(t("saveBeforeNavigation")); return; }
    const proposedName = window.prompt(t("branchPrompt"), "alternative-1")?.trim();
    if (!proposedName) return;
    setBusy(true);
    setActionError(null);
    try {
      await createBranch({ apiOrigin, projectId, name: proposedName, kind: "alternative", auth: { ...baseAuth, branchName: "main" } });
      setActiveBranchName(proposedName);
      setSnapshot(null);
      setDraftOverrides({});
    } catch (error) {
      setActionError(errorMessage(error, t));
    } finally {
      setBusy(false);
    }
  };

  const addScene = async () => {
    if (!remoteEnabled || !snapshot) return;
    if (busy || !saved) { setActionError(t("saveBeforeNavigation")); return; }
    const title = window.prompt(t("newScenePrompt"), `${t("newScene")} ${activeScenes.length + 1}`)?.trim();
    if (!title) return;
    setBusy(true);
    setActionError(null);
    try {
      const result = await createUnit({
        apiOrigin,
        projectId,
        expectedHead: snapshot.branch.headCommitId,
        title,
        type: "scene",
        sortOrder: activeScenes.length,
        auth,
      });
      setWorkspaceView("write");
      setMobileView("write");
      await refresh(undefined, result.unitId);
      setSelectedId(result.unitId);
    } catch (error) {
      setActionError(errorMessage(error, t));
      if (error instanceof StoryClientError && error.code === "HEAD_CONFLICT") await refresh();
    } finally {
      setBusy(false);
    }
  };

  if (remoteEnabled && loading && !snapshot) {
    return <div className="studio-state-screen" role="status"><strong>xHalo Story</strong><p>{t("loading")}</p></div>;
  }
  if (remoteEnabled && !snapshot) {
    return <div className="studio-state-screen error" role="alert"><strong>{t("unavailable")}</strong><p>{actionError}</p><button type="button" onClick={() => { void refresh(); }}>{t("retry")}</button></div>;
  }

  return (
    <div className="story-app">
      {actionError ? <div className="studio-action-alert" role="alert"><span>{actionError}</span>{dirtyDraft ? <button type="button" disabled={busy} onClick={() => setDirtyDraft((draft) => draft ? { ...draft } : null)}>{t("retry")}</button> : null}<button type="button" onClick={() => setActionError(null)} aria-label={t("dismiss")}>×</button></div> : null}
      <TopBar
        head={shortHead(head)}
        {...(snapshot ? { projectName: snapshot.project.name, branchName: snapshot.branch.name } : {})}
        branches={branches.map((branch) => branch.name)}
        {...(remoteEnabled ? { onBranchChange: switchBranch, onCreateBranch: () => { void addBranch(); } } : {})}
        connected={remoteEnabled}
      />
      <div className="desktop-outline-slot"><OutlinePanel scenes={activeScenes} selectedId={selectedScene.id} onSelect={selectScene} totalWords={totalWords} {...(remoteEnabled ? { onCreate: () => { void addScene(); }, creating: busy } : {})} /></div>
      <WorkspaceNav active={workspaceView} onChange={(view) => {
        setWorkspaceView(view);
        telemetry?.track("workspace_switched", ["write", "world", "production", "collaboration"].includes(view) ? view as "write" | "world" | "production" | "collaboration" : "unknown");
      }} />
      {workspaceView === "world" ? (
        <div className="workspace-grid world-workspace">
          <WorldPanel world={world} invalidations={invalidations} onResolve={(id) => { void resolveInvalidation(id); }} />
        </div>
      ) : workspaceView === "version" ? (
        <div className="workspace-grid world-workspace"><VersionPanel commits={commits} /></div>
      ) : workspaceView === "context" ? remoteEnabled ? (
        <div className="workspace-grid world-workspace">
          <ContextAiPanel
            key={`${activeBranchName}:${selectedScene.id}`}
            apiOrigin={apiOrigin}
            projectId={projectId}
            unitId={selectedScene.id}
            unitTitle={selectedScene.title}
            auth={auth}
            onProposal={async () => {
              await refresh(undefined, selectedScene.id);
              setDecision("pending");
              setWorkspaceView("write");
              setMobileView("changes");
            }}
          />
        </div>
      ) : <div className="workspace-grid world-workspace"><section className="workspace-notice"><strong>{t("contextAi")}</strong><p>{t("remoteWorkspaceRequired")}</p></section></div>
      : workspaceView === "intelligence" ? remoteEnabled ? (
        <div className="workspace-grid world-workspace"><IntelligencePanel apiOrigin={apiOrigin} projectId={projectId} auth={auth} /></div>
      ) : <div className="workspace-grid world-workspace"><section className="workspace-notice"><strong>{t("intelligence")}</strong><p>{t("remoteWorkspaceRequired")}</p></section></div>
      : workspaceView === "production" ? remoteEnabled ? (
        <div className="workspace-grid world-workspace"><ProductionPanel key={`${activeBranchName}:${selectedScene.id}`} apiOrigin={apiOrigin} projectId={projectId} unitId={selectedScene.id} unitTitle={selectedScene.title} auth={auth} /></div>
      ) : <div className="workspace-grid world-workspace"><section className="workspace-notice"><strong>{t("production")}</strong><p>{t("remoteWorkspaceRequired")}</p></section></div>
      : workspaceView === "collaboration" ? remoteEnabled ? (
        <div className="workspace-grid world-workspace"><CollaborationWorldGamePanel key={`${activeBranchName}:${selectedScene.id}`} apiOrigin={apiOrigin} projectId={projectId} unitId={selectedScene.id} unitTitle={selectedScene.title} headCommitId={head} auth={auth} /></div>
      ) : <div className="workspace-grid world-workspace"><section className="workspace-notice"><strong>{t("collaborationGame")}</strong><p>{t("remoteWorkspaceRequired")}</p></section></div>
      : (
        <div className="workspace-grid">
          <ManuscriptEditor
            key={selectedScene.id}
            readOnly={busy}
            scene={selectedScene}
            content={visibleContent}
            saved={saved}
            onEdit={edit}
            onPropose={() => { void propose(); }}
            canPropose={canPropose}
            proposing={busy}
            facts={factLabels}
            invalidationCount={openInvalidations.length}
            relationshipCount={world.relationships.length}
            eventCount={world.events.length}
            beliefCount={world.beliefs.length}
            onOpenWorld={() => openWorkspace("world")}
            {...(remoteEnabled ? { onOpenContext: () => openWorkspace("context") } : {})}
          />
          <div className="desktop-inspector-slot">
            <InspectorPanel
              status={reviewStatus}
              {...(pendingChange ? { pendingChange } : {})}
              currentContent={visibleContent}
              facts={factLabels}
              invalidationCount={openInvalidations.length}
              eventCount={world.events.length}
              relationshipCount={world.relationships.length}
              busy={busy}
              error={actionError}
              onAccept={() => { void accept(); }}
              onReject={() => { void reject(); }}
              onOpenWorld={() => openWorkspace("world")}
              {...(remoteEnabled ? { onOpenContext: () => openWorkspace("context") } : {})}
            />
          </div>
        </div>
      )}
      <StatusBar head={shortHead(head)} saved={saved} invalidationCount={openInvalidations.length} connected={remoteEnabled} />
      {mobileView === "outline" ? <OutlinePanel overlay scenes={activeScenes} selectedId={selectedScene.id} onSelect={selectScene} totalWords={totalWords} {...(remoteEnabled ? { onCreate: () => { void addScene(); }, creating: busy } : {})} /> : null}
      {mobileView === "changes" ? (
        <InspectorPanel
          overlay
          status={reviewStatus}
          {...(pendingChange ? { pendingChange } : {})}
          currentContent={visibleContent}
          facts={factLabels}
          invalidationCount={openInvalidations.length}
          eventCount={world.events.length}
          relationshipCount={world.relationships.length}
          busy={busy}
          error={actionError}
          onAccept={() => { void accept(); }}
          onReject={() => { void reject(); }}
          onOpenWorld={() => openWorkspace("world")}
          {...(remoteEnabled ? { onOpenContext: () => openWorkspace("context") } : {})}
        />
      ) : null}
      <MobileNav
        workspace={workspaceView}
        onWorkspace={openWorkspace}
        activeView={mobileView}
        onChange={(view) => { setMobileView(view); if (view === "write") setWorkspaceView("write"); }}
        onPropose={() => { void propose(); }}
        canPropose={canPropose}
        proposing={busy}
      />
    </div>
  );
}
